import * as React from 'react';
import { LeftSmallLine, RightSmallLine } from '@mingcute/react';

import { cn } from '../lib/cn';
import { useMediaQuery } from '../hooks/use-media-query';

export interface PaginationProps extends React.HTMLAttributes<HTMLDivElement> {
  page: number; // 1-based
  pageCount: number;
  pageSize?: number;
  pageSizeOptions?: number[];
  onPageChange: (page: number) => void;
  onPageSizeChange?: (size: number) => void;
  /** Number of page buttons (besides first / last) shown around current. */
  siblingCount?: number;
}

function range(start: number, end: number): number[] {
  const out: number[] = [];
  for (let i = start; i <= end; i += 1) out.push(i);
  return out;
}

function buildPages(
  current: number,
  total: number,
  siblings: number,
): (number | 'ellipsis')[] {
  const totalShown = siblings * 2 + 5; // first + last + current + 2*siblings + 2 ellipses
  if (total <= totalShown) return range(1, total);

  const leftSibling = Math.max(current - siblings, 1);
  const rightSibling = Math.min(current + siblings, total);

  const showLeftEllipsis = leftSibling > 2;
  const showRightEllipsis = rightSibling < total - 1;

  const pages: (number | 'ellipsis')[] = [1];
  if (showLeftEllipsis) pages.push('ellipsis');
  else if (leftSibling === 2) pages.push(2);

  for (let i = Math.max(leftSibling, 2); i <= Math.min(rightSibling, total - 1); i += 1) {
    if (!pages.includes(i)) pages.push(i);
  }

  if (showRightEllipsis) pages.push('ellipsis');
  else if (rightSibling === total - 1 && !pages.includes(total - 1)) {
    pages.push(total - 1);
  }
  if (!pages.includes(total)) pages.push(total);
  return pages;
}

export const Pagination = React.forwardRef<HTMLDivElement, PaginationProps>(
  (
    {
      className,
      page,
      pageCount,
      pageSize,
      pageSizeOptions = [10, 25, 50, 100],
      onPageChange,
      onPageSizeChange,
      siblingCount = 1,
      ...props
    },
    ref,
  ) => {
    const isMobile = !useMediaQuery('(min-width: 640px)');
    const safePageCount = Math.max(1, pageCount);
    const safePage = Math.min(Math.max(1, page), safePageCount);

    const pages = buildPages(safePage, safePageCount, isMobile ? 0 : siblingCount);

    const baseBtn = cn(
      'inline-flex h-8 min-w-8 items-center justify-center rounded-[var(--radius-md)] px-2',
      'text-sm font-medium transition-colors',
      'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-ring)]',
      'disabled:opacity-40 disabled:pointer-events-none',
    );

    return (
      <div
        ref={ref}
        className={cn(
          'flex w-full items-center justify-between gap-3 flex-wrap',
          className,
        )}
        {...props}
      >
        <div className="flex items-center gap-1">
          <button
            type="button"
            className={cn(
              baseBtn,
              'text-[var(--color-fg-secondary)] hover:bg-[var(--color-subtle)]',
            )}
            onClick={() => onPageChange(safePage - 1)}
            disabled={safePage <= 1}
            aria-label="Previous page"
          >
            <LeftSmallLine size={16} />
            {!isMobile ? <span className="ml-1">Prev</span> : null}
          </button>

          {isMobile ? (
            <span className="px-2 text-sm text-[var(--color-fg-secondary)]">
              {safePage} / {safePageCount}
            </span>
          ) : (
            pages.map((p, idx) =>
              p === 'ellipsis' ? (
                <span
                  key={`e-${idx}`}
                  className="inline-flex h-8 min-w-8 items-center justify-center text-[var(--color-fg-muted)]"
                >
                  …
                </span>
              ) : (
                <button
                  key={p}
                  type="button"
                  onClick={() => onPageChange(p)}
                  className={cn(
                    baseBtn,
                    p === safePage
                      ? 'bg-[var(--color-accent)] text-[var(--tvl-on-accent)]'
                      : 'text-[var(--color-fg)] hover:bg-[var(--color-subtle)]',
                  )}
                  aria-current={p === safePage ? 'page' : undefined}
                >
                  {p}
                </button>
              ),
            )
          )}

          <button
            type="button"
            className={cn(
              baseBtn,
              'text-[var(--color-fg-secondary)] hover:bg-[var(--color-subtle)]',
            )}
            onClick={() => onPageChange(safePage + 1)}
            disabled={safePage >= safePageCount}
            aria-label="Next page"
          >
            {!isMobile ? <span className="mr-1">Next</span> : null}
            <RightSmallLine size={16} />
          </button>
        </div>

        {pageSize !== undefined && onPageSizeChange ? (
          <label className="flex items-center gap-2 text-sm text-[var(--color-fg-secondary)]">
            {!isMobile ? <span>Rows</span> : null}
            <select
              value={pageSize}
              onChange={(e) => onPageSizeChange(Number(e.target.value))}
              className={cn(
                'h-8 rounded-[var(--radius-md)] border border-[var(--color-border)] bg-[var(--color-surface)]',
                'px-2 text-sm text-[var(--color-fg)]',
                'focus-visible:outline-none',
              )}
              aria-label="Rows per page"
            >
              {pageSizeOptions.map((opt) => (
                <option key={opt} value={opt}>
                  {opt}
                </option>
              ))}
            </select>
          </label>
        ) : null}
      </div>
    );
  },
);
Pagination.displayName = 'Pagination';
